
import dispatch from '../../utils/event';
import { defineComponent, computed, ref, SetupContext, getCurrentInstance, nextTick, onBeforeUnmount } from 'vue';
import { merge } from './../../utils/merge';
import { getPropByPath } from './../../utils/element';
import { IInvaliField, ITrigger, IRules, IValidationRule, IDescriptor, IValidationError } from './../../typing/form';
import Schema from 'async-validator';
interface IFormModel {
  rules: any;
  model: any;
}
export default defineComponent({
  name: 'TFormItem',
  components: {},
  props: {
    prop: {
      type: String,
      default: '',
      required: true,
    },
    required: {
      type: Boolean,
      default: false,
    },
    rules: {
      type: Array,
      default: () => [],
    },
    label: {
      type: String,
      default: undefined,
    },
    error: {
      type: String,
      default: '',
    },
    isAutoHeight: {
      type: Boolean,
      default: false,
    },
    hasBorder: {
      type: Boolean,
      default: true,
    },
    showMessage: {
      type: Boolean,
      default: true,
    },
    size: {
      type: String,
      default: 'medium',
      validator: (value: string) => ['small', 'medium', 'large'].includes(value),
    },
  },
  emits: ['click', 'validate', 'form.item.focus', 'form.item.blur', 'form.item.change'],
  setup(props, context: SetupContext) {
    const instance = getCurrentInstance();
    /** 组件的className前辍 */
    const prefixCls = ref('t-form-item');

    const validateState = ref('');
    const validateMessage = ref('');

    /** 组件的className前辍 */
    const getClasses = computed(() => {
      return {
        body: [`${prefixCls.value}-bd`, !props.isAutoHeight && `${prefixCls.value}-height__${props.size}`],
      };
    });

    const getForm = computed(() => {
      if (instance) {
        let parent = (instance as any).parent;
        let parentName = (parent as any).type.name || '';
        while (parentName !== 'TForm') {
          parent = (parent as any).parent;
          parentName = (parent as any).type.name || '';
        }
        return parent as any;
      }
      return '';
    });

    const getFiledValue = computed(() => {
      const model = getForm?.value?.props?.model || {};
      if (!model || !props.prop) {
        return undefined;
      }

      return getPropByPath(model, props.prop, true).value;
    });

    const getRules = () => {
      const formRules = getForm?.value?.props?.rules || {};
      const requiredRule = props.required ? { required: !!props.required } : [];
      const prop = getPropByPath(formRules, props.prop || '', false) || [];
      const ruleResult = formRules ? (prop as any).value : [];
      const outputRules = props.rules && props.rules.length ? props.rules : ruleResult;
      return [...outputRules, requiredRule].filter((item: any) => item && item.length !== 0);
    };

    const getFilteredRule = (trigger: ITrigger) => {
      const rules = getRules() as IRules[];
      const rulesFilter = rules.filter(rule => {
        if (!rule.trigger || trigger === '') {
          return true;
        }
        if (Array.isArray(rule.trigger)) {
          return rule.trigger.indexOf(trigger) !== -1;
        }
        return rule.trigger === trigger;
      });
      return rulesFilter.map(rule => merge({}, rule));
    };

    /** 验证表单输入的合法性 */
    const validate = (
      trigger: ITrigger,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      callback = (message?: string, invalidFileds?: IInvaliField) => {
        return;
      },
    ) => {
      let rules = getFilteredRule(trigger);
      if ((!rules || rules.length === 0) && props.required === undefined) {
        callback();
        return;
      }
      validateState.value = 'validating';
      const descriptor: any = {};
      if (rules && rules.length) {
        rules = rules.map((rule: IRules) => {
          delete rule.trigger;
          return rule;
        });
      }
      descriptor[props.prop] = rules as IValidationRule;
      console.log(descriptor);
      const validator = new Schema(descriptor);
      nextTick(() => {
        validator.validate(
          {
            [props.prop]: getFiledValue.value,
          },
          {
            firstFields: true,
          },
          (err: any, invalidFields: any) => {
            validateState.value = err ? 'error' : 'success';
            validateMessage.value = err ? err[0].message : '';
            context.emit('validate', validateState.value, err, getFiledValue.value);
            callback(validateMessage.value, invalidFields);
          },
        );
      });
    };

    dispatch.emit('form.item.add', {
      parentUid: getForm?.value?.uid || 0,
      value: instance?.proxy || undefined,
    });

    dispatch.on('form.item.change', res => {
      const uid = instance?.uid || 0;
      if (uid && res.parentUid === uid) {
        validate('change');
      }
    });

    dispatch.on('form.item.blur', res => {
      const uid = instance?.uid || 0;
      if (uid && res.parentUid === uid) {
        validate('blur');
      }
    });

    onBeforeUnmount(() => {
      dispatch.emit('form.item.remove', {
        parentUid: getForm?.value?.uid || 0,
        value: instance?.proxy || undefined,
      });
    });

    /** 对外响应的事件 */
    const listener = (event: any) => context.emit('click', event);
    return {
      prefixCls,
      getClasses,
      validateState,
      getForm,
      getFiledValue,
      getRules,
      getFilteredRule,
      validateMessage,
      listener,
      validate,
    };
  },
});
